import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { setAccessToken, setRefreshHandler } from '@/api/client'
import { preferences, setRemotePersistence } from '@/features/preferences/store'
import { moduleEnabled } from '@/features/school/modules'
import { authApi } from './api'
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext'
import type { CurrentUser, LoginInput, Permission, Portal, SchoolSummary, Session } from './types'

/** Queries about the address, not the person: kept (and never cancelled) when the session ends. */
const PUBLIC_QUERIES = new Set<unknown>(['site-context', 'branding'])

/**
 * Owns the session: restores it on boot from the refresh cookie, schedules a proactive refresh
 * shortly before the access token expires, and exposes permission checks.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [school, setSchool] = useState<SchoolSummary | null>(null)
  const [portal, setPortal] = useState<Portal | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const clear = useCallback(() => {
    window.clearTimeout(timer.current)
    setRemotePersistence(null)
    setAccessToken(null)
    setUser(null)
    setSchool(null)
    setPortal(null)
    setStatus('anonymous')
    // Drop everything the signed-in person loaded, but not the site context: removing it while it
    // is still loading (a signed-out visitor's first page) would leave the page waiting forever.
    queryClient.removeQueries({ predicate: (q) => !PUBLIC_QUERIES.has(q.queryKey[0]) })
  }, [queryClient])

  // Holds the latest `apply` so the proactive-refresh timer can re-schedule itself.
  const applyRef = useRef<(session: Session) => void>(() => undefined)

  const apply = useCallback(
    (session: Session) => {
      setAccessToken(session.accessToken)
      // The account's saved language/theme win over this browser's last choice.
      preferences.adopt(session.user.preferences)
      setUser(session.user)
      setSchool(session.school)
      setPortal(session.portal)
      setStatus('authenticated')
      // While signed in, preference changes are saved to the account (best effort).
      setRemotePersistence((prefs) => {
        authApi.updatePreferences(prefs).then(setUser, () => undefined)
      })
      window.clearTimeout(timer.current)
      // Refresh one minute before expiry (min 10 s).
      const ms = Math.max(10, session.expiresIn - 60) * 1000
      timer.current = window.setTimeout(() => {
        void authApi.refresh().then((s) => applyRef.current(s), clear)
      }, ms)
    },
    [clear],
  )

  useEffect(() => {
    applyRef.current = apply
  }, [apply])

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const session = await authApi.refresh()
      apply(session)
      return session.accessToken
    } catch {
      clear()
      return null
    }
  }, [apply, clear])

  useEffect(() => {
    setRefreshHandler(refresh)
    // Restore the session from the refresh cookie on boot.
    authApi.refresh().then(apply, clear)
    return () => {
      setRefreshHandler(null)
      window.clearTimeout(timer.current)
    }
  }, [refresh, apply, clear])

  const login = useCallback(
    async (input: LoginInput) => {
      const session = await authApi.login(input)
      queryClient.removeQueries({ predicate: (q) => !PUBLIC_QUERIES.has(q.queryKey[0]) })
      apply(session)
      return session
    },
    [apply, queryClient],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      clear()
    }
  }, [clear])

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set<Permission>(user?.permissions ?? [])
    return {
      status,
      user,
      school,
      portal,
      login,
      logout,
      can: (p) => permissions.has(p),
      hasModule: (m) => moduleEnabled(school?.modules, m),
      setUser,
      setSchool,
    }
  }, [status, user, school, portal, login, logout])

  return <AuthContext value={value}>{children}</AuthContext>
}
